import closeIcon from "../../icons/close.svg?raw";
import {
  cloneElement,
  closest,
  contains,
  createElement,
  focusNextChild,
  focusPreviousChild,
  getAttributes,
  replaceChildren,
  uniqueId,
} from "../utils";

export default class TagList extends HTMLElement {
  /**
   * Define the element in the document's custom element registry.
   *
   * @param {string} [tag] The tag to use in the element definition
   */
  static define(tag = "ifrs-taglist") {
    if (!customElements.get(tag)) {
      customElements.define(tag, this);
    }
  }

  /**
   * A collection for side effect cleanup functions. Each function in the set
   * is called and removed when the component is disconnected.
   *
   * @type {Set<() => void>}
   */
  cleanup = new Set();

  /**
   * Sets up the component on mount.
   */
  connectedCallback() {
    this.setup();

    /**
     * @param {*} el The dispatching EventTarget
     */
    const dispatchDismissal = (el) => {
      const event = new Event("dismiss", {
        bubbles: true,
        cancelable: true,
        composed: true,
      });

      if (el instanceof EventTarget) {
        el.dispatchEvent(event);
      }
    };

    /**
     * @param {PointerEvent} event A click event
     */
    const handleClick = (event) => {
      const button = closest(event.target, "[role='button']");
      const cell = closest(button, "[role='row']")?.querySelector("[role='gridcell']");
      if (contains(this, cell)) {
        dispatchDismissal(cell);
      }
    };

    /**
     * @param {FocusEvent} event A focusin event
     */
    const handleFocusIn = (event) => {
      if (event.target instanceof HTMLElement && closest(event.target, "[role='gridcell']")) {
        event.target.tabIndex = 0;
      }
      if (
        event.relatedTarget instanceof HTMLElement &&
        closest(event.relatedTarget, "[role='gridcell']") &&
        contains(this, event.relatedTarget)
      ) {
        event.relatedTarget.tabIndex = -1;
      }
    };

    /**
     * @param {KeyboardEvent} event A keydown event
     */
    const handleKeydown = (event) => {
      if ("ArrowDown" === event.key || "ArrowRight" === event.key) {
        event.preventDefault();
        focusNextChild(this);
      }
      if ("ArrowLeft" === event.key || "ArrowUp" === event.key) {
        event.preventDefault();
        focusPreviousChild(this);
      }
      if ("Backspace" === event.key) {
        const cell = closest(event.target, "[role='row']")?.querySelector("[role='gridcell']");
        if (contains(this, cell)) {
          event.preventDefault();
          dispatchDismissal(cell);
        }
      }
      if (" " === event.key || "Enter" === event.key) {
        const button = closest(event.target, "[role='button']");
        const cell = closest(button, "[role='row']")?.querySelector("[role='gridcell']");
        if (contains(this, cell)) {
          event.preventDefault();
          dispatchDismissal(cell);
        }
      }
    };

    this.addEventListener("click", handleClick);
    this.addEventListener("focusin", handleFocusIn);
    this.addEventListener("keydown", handleKeydown);

    this.cleanup.add(() => {
      this.removeEventListener("click", handleClick);
      this.removeEventListener("focusin", handleFocusIn);
      this.removeEventListener("keydown", handleKeydown);
    });
  }

  /**
   * Cleans up side effects when the component is disconnected.
   */
  disconnectedCallback() {
    // Call and delete all cleanup functions
    if (this.cleanup) {
      this.cleanup.forEach((fn) => fn());
      this.cleanup.clear();
    }
  }

  /**
   * Initializes the component properties and markup.
   *
   * The function may be called repeatedly and should be idempotent.
   */
  setup() {
    if (!this.id) {
      this.id = uniqueId(this.tagName.toLowerCase());
    }

    const listitems = this.querySelectorAll("li");

    if (listitems.length > 0) {
      this.setAttribute("role", "grid");
      this.classList.add("taglist");

      replaceChildren(
        this,
        Array.from(listitems).map((el, index) => {
          const id = `${this.id}-${index}`;

          const icon = cloneElement(closeIcon);
          if (icon) {
            icon.classList.add("icon");
            icon.removeAttribute("xmlns");
          }

          return createElement("div", { ...getAttributes(el), classList: "tag", role: "row" }, [
            createElement(
              "span",
              { ...getAttributes(el), role: "gridcell" },
              createElement("span", {
                id,
                innerHTML: el.innerHTML,
                tabIndex: index > 0 ? -1 : 0,
              }),
            ),
            createElement(
              "span",
              { role: "gridcell" },
              createElement(
                "span",
                {
                  "aria-label": "Remove item",
                  "aria-labelledby": `${id} ${id}button`,
                  id: `${id}button`,
                  role: "button",
                  tabIndex: -1,
                },
                icon,
              ),
            ),
          ]);
        }),
      );
    }
  }
}
